// O celular da conta na tela (canvas 0.3 e 0.5): o campo com "🇧🇷 +55" e a máscara, a caixa dos avisos pelo WhatsApp
// e a folha "Falta seu celular", que abre quando a mesa recusa sentar sem celular e depois de guardar a conta com
// Google. No computador, a folha abre como diálogo no centro.
import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { ErroDaApi } from './api.ts';
import { avisoDoCelular, celularValido, guardarMeuCelular, mascararDigitando } from './celular.ts';
import type { Sessao } from './conta.ts';

export const ROTULO_CELULAR = 'Celular com DDD';
export const TEXTO_AVISOS = 'Quero receber avisos e convites do Truco de 6 no WhatsApp (opcional)';

export function CampoCelular({ valor, aoMudar, ajuda, erro, autoFocus = false }: {
  valor: string; aoMudar: (v: string) => void; ajuda: string; erro: string | null; autoFocus?: boolean;
}) {
  const id = useId();
  const aviso = erro ?? avisoDoCelular(valor);
  return (
    <div className="campo campo-celular">
      <label htmlFor={id}>{ROTULO_CELULAR}</label>
      <div className="linha-celular">
        <span className="ddi" aria-hidden="true">🇧🇷 +55</span>
        <input id={id} type="tel" inputMode="tel" autoComplete="tel-national" placeholder="(11) 98765-4321" value={valor} maxLength={16}
          autoFocus={autoFocus} aria-invalid={!!aviso} aria-describedby={id + 'a'} onChange={(ev) => aoMudar(mascararDigitando(ev.target.value))} />
      </div>
      {aviso ? <small id={id + 'a'} className="erro" role="alert">{aviso}</small> : <small id={id + 'a'}>{ajuda}</small>}
    </div>
  );
}

export function CaixaAvisos({ marcada, aoMudar }: { marcada: boolean; aoMudar: (v: boolean) => void }) {
  return (
    <label className="aceite avisos-whats">
      <input type="checkbox" checked={marcada} onChange={(ev) => aoMudar(ev.target.checked)} />
      <span>{TEXTO_AVISOS}</span>
    </label>
  );
}

// botao: SALVAR E SENTAR quando a folha abriu ao sentar; SALVAR depois de guardar a conta com Google.
export function FolhaCelular({ sessao, botao = 'SALVAR E SENTAR', aoSalvar, aoFechar }: {
  sessao: Sessao; botao?: string; aoSalvar: () => void; aoFechar: () => void;
}) {
  const [numero, setNumero] = useState('');
  const [avisos, setAvisos] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function salvar(ev: React.FormEvent) {
    ev.preventDefault();
    if (!celularValido(numero)) return;
    setOcupado(true); setErro(null);
    try {
      await guardarMeuCelular(sessao, numero, avisos);
      aoSalvar();
    } catch (e) {
      setErro(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
      setOcupado(false);
    }
  }
  return createPortal(
    <div className="folha-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget && !ocupado) aoFechar(); }}>
      <form className="folha simples folha-celular" role="dialog" aria-modal="true" aria-labelledby="folha-celular" aria-describedby="folha-celular-texto"
        onSubmit={(ev) => void salvar(ev)}>
        {/* Os dois lados viram colunas com o celular deitado; em pé, um embaixo do outro. */}
        <div className="folha-celular-lado">
          <h2 id="folha-celular">Falta seu celular</h2>
          <p id="folha-celular-texto">Para sentar na mesa, informe o seu celular. Ele serve para contato e segurança da conta, e cada número fica em uma conta só.</p>
        </div>
        <div className="folha-celular-lado">
          <CampoCelular valor={numero} aoMudar={(v) => { setNumero(v); setErro(null); }} ajuda="Ninguém na mesa vê o seu número." erro={erro} autoFocus />
          <CaixaAvisos marcada={avisos} aoMudar={setAvisos} />
          <button className="btn1 largo" type="submit" disabled={ocupado || !celularValido(numero)}>{ocupado ? 'UM INSTANTE...' : botao}</button>
          <button className="btn2" type="button" disabled={ocupado} onClick={aoFechar}>Agora não</button>
        </div>
        <p className="nota">Como usamos o seu número: <a href="/privacidade" target="_blank" rel="noreferrer">Política de privacidade</a>.</p>
      </form>
    </div>,
    document.body,
  );
}
